import { Box, IconButton, Link, Snackbar, SnackbarProps, Stack, Typography } from '@mui/material'
import { colours } from '../StyleConstants'
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import { Close } from '@mui/icons-material';
import { MouseEventHandler, useEffect, useState } from 'react';

type LassoSnackbarProps = SnackbarProps & {
    variant: "default" | "system" | "error" | "critical" | "warning" | "success" | "info",
    message: string,
    closable?: boolean,
    close?: Function,
    onDetailsClick?: MouseEventHandler<HTMLAnchorElement>,
}
const defaultProps: Partial<LassoSnackbarProps> = {
    variant: "default",
    closable: true,
}

const snackbarVariations = {
    default: {
        primary: colours.midnightBlue.blue160,
        secondary: colours.midnightBlue.blue5,
        borderColor: colours.midnightBlue.blue160,
        icon: <ErrorOutlineOutlinedIcon style={{ color: colours.midnightBlue.blue160 }} />
    },
    system: {
        primary: colours.orange.orange110,
        secondary: colours.orange.orange15,
        borderColor: colours.orange.orange110,
        icon: <RotateLeftIcon style={{ color: colours.orange.orange110 }} />
    },
    error: {
        primary: colours.error.error,
        secondary: colours.error.error20,
        borderColor: colours.error.error,
        icon: <ErrorOutlineOutlinedIcon style={{ color: colours.error.error }} />
    },
    critical: {
        primary: colours.core.white,
        secondary: colours.error.error,
        borderColor: colours.error.error,
        icon: <ErrorOutlineOutlinedIcon style={{ color: colours.core.white }} />
    },
    warning: {
        primary: colours.warning.warn,
        secondary: colours.warning.warn10,
        borderColor: colours.warning.warn,
        icon: <ErrorOutlineOutlinedIcon style={{ color: colours.warning.warn }} />
    },
    success: {
        primary: colours.success.success,
        secondary: colours.success.success20,
        borderColor: colours.success.success,
        icon: <CheckCircleOutlineIcon style={{ color: colours.success.success }} />
    },
    info: {
        primary: colours.notifications.info,
        secondary: colours.notifications.info20,
        borderColor: colours.notifications.info,
        icon: <ErrorOutlineOutlinedIcon style={{ color: colours.notifications.info }} />
    }

}

const LassoSnackbar = (props: LassoSnackbarProps) => {
    const snackbarProps = { ...props };
    delete snackbarProps.closable; //This would otherwise be passed into snackbar, causing a console warning.
    props = { ...defaultProps, ...props }
    const textColor = snackbarVariations[props.variant].primary;
    const bgColor = snackbarVariations[props.variant].secondary;
    const borderColor = snackbarVariations[props.variant].borderColor;
    const icon = snackbarVariations[props.variant].icon;

    const [isOpen, setIsOpen] = useState(props.open);

    useEffect(() => {
        setIsOpen(props.open)
    }, [props])


    return (
        <Snackbar
            {...snackbarProps}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            open={isOpen}
        >
            <Box sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                backgroundColor: bgColor,
                boxShadow: "2px 8px 8px 3px #15181840",
                borderRadius: "5px",
                borderLeft: `5px solid ${borderColor}`,
                gap: 2,
                padding: "10px",
                minHeight: "54px"
            }}>
                <Stack direction="row" gap={2} alignItems="center">
                    {icon}
                    <Typography
                        color={textColor}
                        fontSize="14px"
                        fontWeight={600}
                    >
                        {props.message}
                    </Typography>
                    {
                        props.onDetailsClick ?
                            <Link
                                onClick={props.onDetailsClick}
                                fontSize="14px"
                                color={textColor}
                                marginLeft="16px"
                                underline="none"
                                sx={{ cursor: "pointer", ":hover": { textDecoration: "underline" } }}
                            >
                                View Details
                            </Link> : <></>
                    }

                </Stack>
                {props.closable ?
                    <IconButton
                        size="small"
                        aria-label="close"
                        color="inherit"
                        onClick={() => {
                            setIsOpen(false);
                            if (props.close) {
                                props.close();
                            }
                        }}
                    >
                        <Close sx={{ width: "20px", cursor: "pointer", color: props.variant !== "critical" ? colours.midnightBlue.blue80 : colours.core.white }} />
                    </IconButton>
                    : null}
            </Box>
        </Snackbar >
    )
}

export default LassoSnackbar